/**
 * 個別閲覧画面に出す内容を、サーバーヘッドと端末ドラフトから選ぶ。
 *
 * 閲覧画面は保存処理を持たない。ただし編集直後や通信断中にも「さっき書いた内容」が
 * 古いヘッドへ見え戻りしないよう、エディタと同じ復元判定を使う。他端末のヘッドが
 * 先行しているときはドラフトを本文へ適用せず、編集画面の履歴退避経路に任せる。
 */

import type { DraftRecord } from '@app/storage/draft-buffer';
import { decideDraftRestore } from '@shared/save-protocol';
import type { JstDate, Note } from '@shared/types';

export interface NoteViewContent {
  title: string | null;
  body: string;
  category_id: number | null;
  subject_date: JstDate;
  subject_date_end: JstDate | null;
}

export type NoteViewSource = 'head' | 'local_draft' | 'head_with_orphan_draft';

export interface NoteViewResolution {
  content: NoteViewContent;
  source: NoteViewSource;
}

function contentOf(value: Note | DraftRecord): NoteViewContent {
  return {
    title: value.title,
    body: value.body,
    category_id: value.category_id,
    subject_date: value.subject_date,
    subject_date_end: value.subject_date_end,
  };
}

/**
 * ヘッドを取得できないときは、未送信ドラフトだけをフォールバック表示する。
 * 同期済みドラフトは古い可能性があるため、オフライン閲覧キャッシュとしては使わない。
 */
export function resolveNoteView(
  head: Note | null,
  draft: DraftRecord | null,
): NoteViewResolution | null {
  if (!head) {
    return draft?.dirty ? { content: contentOf(draft), source: 'local_draft' } : null;
  }

  // dirty=false はサーバー送信済みの端末キャッシュ。他端末でヘッドが進んでいても
  // 「未送信の孤児ドラフト」ではないので、常に現在のヘッドを表示する。
  if (!draft?.dirty) {
    return { content: contentOf(head), source: 'head' };
  }

  const decision = decideDraftRestore(draft, head);
  if (decision.kind === 'restore_draft' && draft) {
    return { content: contentOf(draft), source: 'local_draft' };
  }
  if (decision.kind === 'rescue_orphan_draft') {
    return { content: contentOf(head), source: 'head_with_orphan_draft' };
  }
  return { content: contentOf(head), source: 'head' };
}
